import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { api } from '../api/client'
import { useAuth } from '../auth/AuthContext'

/**
 * Der Hinweisdienst der Zentrale.
 *
 * Er holt regelmäßig eine kleine Zahl vom Server und setzt daraus das Zeichen an den
 * Menüpunkten, den Zähler im Fenstertitel und — bei Zuwachs — einen kurzen Ton.
 *
 * **Warum abgefragt und nicht zugestellt:** Vor der Anwendung stehen zwei Tomcats hinter
 * einem Loadbalancer. Eine gehaltene Verbindung (SSE, WebSocket) hängt an genau einer
 * Instanz; eine Nachricht, die auf der anderen eintrifft, käme dort nie an. Die Frage
 * „ist etwas Neues da?" ist zu klein, um dafür einen gemeinsamen Nachrichtenweg zwischen
 * den Instanzen zu bauen.
 */

const TAKT_MS = 20_000
const SCHLUESSEL_TON = 'vlzhub.hinweiston'
const TITEL = 'VLZHUB — Multi-Tenant Bus-Operations'

interface HinweisAntwort {
  ungeleseneNachrichten: number
  offeneUnfaelle: number
  offeneFahrzeugmeldungen: number
  verkehrsmeldungenZuPruefen: number
  offeneKlaerungen: number
  stand: string
}

interface HinweisWerte {
  /** Ungelesene Beiträge — das Zeichen am Menüpunkt „Nachrichten". */
  nachrichten: number
  /** Offene Unfall- und Fahrzeugmeldungen zusammen — das Zeichen an „Meldungen". */
  meldungen: number
  /** Verkehrsmeldungen aus Posteingang und Upload, die noch niemand geprüft hat. */
  verkehrsmeldungen: number
  /** Fragen des Assistenten, auf die noch niemand geantwortet hat. */
  klaerungen: number
  offeneUnfaelle: number
  tonAn: boolean
  setzeTon: (an: boolean) => void
  /** Sofort nachfragen, statt den nächsten Takt abzuwarten. */
  aktualisiere: () => void
}

const LEER: HinweisWerte = {
  nachrichten: 0,
  meldungen: 0,
  verkehrsmeldungen: 0,
  klaerungen: 0,
  offeneUnfaelle: 0,
  tonAn: false,
  setzeTon: () => {},
  aktualisiere: () => {},
}

const HinweisKontext = createContext<HinweisWerte>(LEER)

export function useHinweise() {
  return useContext(HinweisKontext)
}

/**
 * Zwei kurze Töne über die Web-Audio-Schnittstelle.
 *
 * Keine Tondatei: Sie müsste ausgeliefert, zwischengespeichert und von der
 * Inhaltsrichtlinie zugelassen werden — für zwei Sinustöne zuviel Aufwand.
 *
 * Hat der Anwender die Seite noch nicht angefasst, verweigert der Browser die Tonausgabe.
 * Das ist kein Fehler, sondern seine Autoplay-Sperre; das Zeichen am Menüpunkt erscheint
 * trotzdem. Nach dem ersten Klick irgendwo in der Anwendung klingt es.
 */
let tonwerk: AudioContext | null = null

function ping() {
  try {
    const Werk = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Werk) return
    tonwerk = tonwerk ?? new Werk()
    void tonwerk.resume()
    const jetzt = tonwerk.currentTime
    const klang = (versatz: number, hertz: number) => {
      const quelle = tonwerk!.createOscillator()
      const lautstaerke = tonwerk!.createGain()
      quelle.type = 'sine'
      quelle.frequency.value = hertz
      lautstaerke.gain.setValueAtTime(0.0001, jetzt + versatz)
      lautstaerke.gain.exponentialRampToValueAtTime(0.18, jetzt + versatz + 0.02)
      lautstaerke.gain.exponentialRampToValueAtTime(0.0001, jetzt + versatz + 0.3)
      quelle.connect(lautstaerke)
      lautstaerke.connect(tonwerk!.destination)
      quelle.start(jetzt + versatz)
      quelle.stop(jetzt + versatz + 0.32)
    }
    klang(0, 880)
    klang(0.15, 1318)
  } catch {
    // Ton ist Beiwerk. Wenn der Browser nicht mag, bleibt es beim sichtbaren Hinweis.
  }
}

export function HinweisProvider({ children }: { children: ReactNode }) {
  const { benutzer, hatRolle } = useAuth()

  // Der Plattform-Administrator hat weder Nachrichten noch Meldungen — für ihn gäbe der
  // Endpunkt ein Verbot zurück, und die Menüpunkte fehlen ihm ohnehin.
  const zustaendig = Boolean(benutzer) && hatRolle('ADMIN', 'DISPONENT', 'PERSONAL', 'WERKSTATT')

  const [stand, setStand] = useState<HinweisAntwort | null>(null)
  const [tonAn, setTonAnZustand] = useState(() => {
    try {
      return localStorage.getItem(SCHLUESSEL_TON) !== 'aus'
    } catch {
      return true
    }
  })

  const vorheriger = useRef<HinweisAntwort | null>(null)
  const tonRef = useRef(tonAn)
  tonRef.current = tonAn

  const setzeTon = useCallback((an: boolean) => {
    setTonAnZustand(an)
    try {
      localStorage.setItem(SCHLUESSEL_TON, an ? 'an' : 'aus')
    } catch {
      // Ohne Speicher gilt die Einstellung eben nur für diese Sitzung.
    }
    // Der Klick selbst ist die Nutzerhandlung, die dem Browser die Tonausgabe erlaubt —
    // deshalb hier gleich einmal hören lassen, was man eingeschaltet hat.
    if (an) ping()
  }, [])

  const aktualisiere = useCallback(async () => {
    if (!zustaendig) return
    try {
      const neu = await api.get<HinweisAntwort>('/api/hinweise')
      const alt = vorheriger.current
      // Beim ersten Abruf gibt es nichts zu vergleichen — sonst pingte es bei jedem
      // Seitenaufruf, obwohl nichts geschehen ist.
      if (
        alt &&
        tonRef.current &&
        (neu.ungeleseneNachrichten > alt.ungeleseneNachrichten ||
          neu.offeneUnfaelle > alt.offeneUnfaelle ||
          neu.offeneFahrzeugmeldungen > alt.offeneFahrzeugmeldungen ||
          neu.verkehrsmeldungenZuPruefen > alt.verkehrsmeldungenZuPruefen ||
          (neu.offeneKlaerungen ?? 0) > (alt.offeneKlaerungen ?? 0))
      ) {
        ping()
      }
      vorheriger.current = neu
      setStand(neu)
    } catch {
      // Ein ausgefallener Abruf ist kein Ereignis für den Anwender: Der bisherige Stand
      // bleibt stehen, der nächste Takt versucht es erneut.
    }
  }, [zustaendig])

  useEffect(() => {
    if (!zustaendig) {
      setStand(null)
      vorheriger.current = null
      return
    }
    void aktualisiere()
    const takt = window.setInterval(() => void aktualisiere(), TAKT_MS)

    // Wer den Tab wieder nach vorn holt, will den aktuellen Stand sehen und nicht bis zu
    // zwanzig Sekunden auf ihn warten.
    const beiSichtbar = () => {
      if (document.visibilityState === 'visible') void aktualisiere()
    }
    document.addEventListener('visibilitychange', beiSichtbar)
    return () => {
      window.clearInterval(takt)
      document.removeEventListener('visibilitychange', beiSichtbar)
    }
  }, [zustaendig, aktualisiere])

  const nachrichten = stand?.ungeleseneNachrichten ?? 0
  const meldungen = (stand?.offeneUnfaelle ?? 0) + (stand?.offeneFahrzeugmeldungen ?? 0)
  const verkehrsmeldungen = stand?.verkehrsmeldungenZuPruefen ?? 0
  const klaerungen = stand?.offeneKlaerungen ?? 0

  // Der Titel trägt den Hinweis auch dann, wenn die Anwendung in einem Tab im Hintergrund
  // liegt — in der Zentrale steht sie neben Fahrplan und Mailprogramm.
  useEffect(() => {
    const gesamt = nachrichten + meldungen + verkehrsmeldungen + klaerungen
    document.title = gesamt > 0 ? `(${gesamt}) ${TITEL}` : TITEL
    return () => {
      document.title = TITEL
    }
  }, [nachrichten, meldungen, verkehrsmeldungen, klaerungen])

  return (
    <HinweisKontext.Provider
      value={{
        nachrichten,
        meldungen,
        verkehrsmeldungen,
        klaerungen,
        offeneUnfaelle: stand?.offeneUnfaelle ?? 0,
        tonAn,
        setzeTon,
        aktualisiere: () => void aktualisiere(),
      }}
    >
      {children}
    </HinweisKontext.Provider>
  )
}
